import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageTitle } from "@/hooks/use-page-title";
import { authApi } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Landing page for the emailed verification link (`/verify-email?token=...`).
 * Verifies the address, signs the user in, and lands them on the dashboard.
 * A used/expired link offers to send a fresh one.
 */
export function VerifyEmailPage() {
  usePageTitle("Verify email");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const token = params.get("token");
  const [error, setError] = useState<string | null>(token ? null : "This verification link is incomplete.");
  const [email, setEmail] = useState("");
  const [resendNote, setResendNote] = useState("");
  const started = useRef(false);

  useEffect(() => {
    // Tokens are single-use: guard against React StrictMode's double effect.
    if (!token || started.current) return;
    started.current = true;
    authApi
      .verifyEmail(token)
      .then((session) => {
        setSession(session);
        void navigate("/dashboard", { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Could not verify your email.");
      });
  }, [token, navigate, setSession]);

  const resend = async () => {
    try {
      const res = await authApi.resendVerification(email);
      setResendNote(res.message);
    } catch (err) {
      setResendNote(err instanceof ApiError ? err.message : "Could not send the email.");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10">
      <BrandMark />
      <div className="mt-10 w-full max-w-md rounded-lg border border-border bg-card p-8 text-center">
        {error ? (
          <div className="space-y-4">
            <h1 className="text-xl font-semibold text-foreground">Verification failed</h1>
            <p className="text-sm text-muted-foreground" role="alert">
              {error}
            </p>
            <form
              className="space-y-3 text-left"
              onSubmit={(e) => {
                e.preventDefault();
                void resend();
              }}
            >
              <Input
                type="email"
                aria-label="Email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                required
              />
              <Button type="submit" variant="outline" className="w-full">
                Send a new link
              </Button>
            </form>
            {resendNote && <p className="text-xs text-muted-foreground">{resendNote}</p>}
            <Link to="/login" className="block text-sm text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3" role="status">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
            <p className="text-sm text-muted-foreground">Verifying your email...</p>
          </div>
        )}
      </div>
    </div>
  );
}
